// src/ui/lifecycleActivation/LifecycleActivationContext.js
//
// App-level lifecycle-activation provider. Owns the review flow that runs
// BEFORE opening→active, closing→closed, or active→closing (statusOnly):
// PeriodSummaryModal (via LifecycleActivationDock), job peek, then the RPC.
//
// Scope: client-only session lifecycle (same as BulkRunProvider). A page
// reload or sign-out clears any in-flight activation — accepted tradeoff.
//
// Pattern: eager PROVIDER (cheap state only, no heavy UI imports) + lazy DOCK
// (PeriodSummaryModal + JobDetail). Mirror of BulkRunProvider / BulkRunDock.

import { withTimeout } from '../../utils/async/withTimeout';
import { ACTION_BEAT_TIMEOUT_MS } from '../../hooks/ui/useModalActionBeat';
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';

const LifecycleActivationContext = createContext(null);

// Named export used by tests to verify the reset shape.
export const CLOSED_STATE = {
  flow: null,
  summaryVisible: false,
  viewingJobId: null,
  refreshNonce: 0,
  activating: false,
};

export function LifecycleActivationProvider({ children, signedIn }) {
  const [flow, setFlow] = useState(null);
  const [summaryVisible, setSummaryVisible] = useState(false);
  const [viewingJobId, setViewingJobId] = useState(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [activating, setActivating] = useState(false);

  // Mutable mirror so async callbacks always read the current flow without
  // re-subscribing — same pattern as BulkRunContext's runRef.
  const flowRef = useRef(null);
  flowRef.current = flow;

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const resetAll = useCallback(() => {
    setFlow(null);
    setSummaryVisible(false);
    setViewingJobId(null);
    setRefreshNonce(0);
    setActivating(false);
  }, []);

  // Security: tie the review flow's lifetime to the session. When the App flips
  // `signedIn` to false (logout / idle timeout / token expiry / cross-tab
  // sign-out) reset it so the PeriodSummaryModal can't paint over the login
  // screen or resurrect on same-tab re-login. `signedIn` is undefined in
  // isolated provider tests, so the `=== false` guard leaves them untouched.
  useEffect(() => {
    if (signedIn === false) resetAll();
  }, [signedIn, resetAll]);

  // ── Public API — entry points call this instead of the transition directly ──
  const beginActivation = useCallback(({
    customerId, customerName, phase, onConfirmed, reviewOnly = false,
    billingSnoozedUntil = null, statusOnly = false, customer = null,
  }) => {
    setFlow({
      customerId, customerName, phase, onConfirmed, reviewOnly,
      billingSnoozedUntil, statusOnly, customer,
    });
    setSummaryVisible(true);
    setViewingJobId(null);
    setRefreshNonce(0);
    setActivating(false);
  }, []);

  // ── Internal handlers — used exclusively by the Dock ──────────────────────

  /**
   * Run the real transition RPC. Boolean-honest (action-beat contract,
   * 2026-08-20): returns true on success, false on failure/no-op — NEVER
   * undefined, so a caller driving a ConfirmDialog/ModalActionBeat off this
   * can't mistake a failure for success.
   *
   * Deliberately does NOT resetAll() on success. PeriodSummaryModal (the
   * dock's confirm) plays the working → success-hold → morph-close action
   * beat itself and calls finishActivation() once that beat's own terminal
   * fires — resetting here would flip `flow` to null and unmount the modal
   * on the very next render, before the beat ever gets to paint. `activating`
   * therefore stays true (buttons stay disabled) through the whole beat
   * window; only finishActivation() clears it.
   */
  const activate = useCallback(async (includeJobIds, serviceStartDate, excludeJobIds) => {
    const currentFlow = flowRef.current;
    if (!currentFlow) return false;
    // reviewOnly mode has no Activate button, but guard defensively.
    if (currentFlow.reviewOnly) return false;
    setActivating(true);
    try {
      // `onConfirmed` returns false to signal failure — keep the summary OPEN so
      // the user can retry (the handler surfaces its own error toast). Any other
      // return value is treated as success. A throw is treated as failure too.
      // `includeJobIds` (2026-09-18 contract) — the sheet's ticked "other" job
      // ids, forwarded so the real transition RPC can include them in the SAME
      // call as the status change. Undefined for callers that don't pass it
      // (reviewOnly / pre-sheet flows) — onConfirmed treats that as "none".
      const result = await withTimeout(currentFlow.onConfirmed(includeJobIds, serviceStartDate, excludeJobIds), ACTION_BEAT_TIMEOUT_MS) /* hung-RPC escape (final review 2026-08-20): requestClose+X are guarded on `activating`, so without this cap an iOS-backgrounded RPC strands the dock — the catch below already clears activating and returns false */;
      if (result === false) {
        setActivating(false);
        return false;
      }
      return true;
    } catch {
      setActivating(false);
      return false;
    }
  }, []);

  /** Dismiss without running the transition. */
  const cancelActivation = useCallback(() => {
    resetAll();
  }, [resetAll]);

  /**
   * Called by PeriodSummaryModal once its success action-beat finishes
   * playing (working → success hold → morph close) — the beat's terminal,
   * mirroring cancelActivation's reset exactly (both mean "the flow is over,
   * tear it down"), kept as a distinctly-named function so a future success-
   * only side effect (analytics, etc.) has an unambiguous home.
   */
  const finishActivation = useCallback(() => {
    resetAll();
  }, [resetAll]);

  /** User tapped a job row in the summary — open JobDetail, hide summary. */
  const openJob = useCallback((jobId) => {
    setViewingJobId(jobId);
    setSummaryVisible(false);
  }, []);

  /** JobDetail dismissed — return to the summary. Refetch is onSave only. */
  const closeJob = useCallback(() => {
    setViewingJobId(null);
    setSummaryVisible(true);
  }, []);

  /**
   * Bump the refresh nonce so PeriodSummaryModal re-fetches the bundle.
   * Used after the quick-dedup resolver deletes jobs, so the summary updates
   * without requiring the user to close and reopen.
   */
  const refreshSummary = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const value = useMemo(() => ({
    // State
    flow,
    summaryVisible,
    viewingJobId,
    refreshNonce,
    activating,
    // API
    beginActivation,
    activate,
    cancelActivation,
    finishActivation,
    openJob,
    closeJob,
    refreshSummary,
  }), [
    flow, summaryVisible, viewingJobId, refreshNonce, activating,
    beginActivation, activate, cancelActivation, finishActivation, openJob, closeJob, refreshSummary,
  ]);

  return (
    <LifecycleActivationContext.Provider value={value}>
      {children}
    </LifecycleActivationContext.Provider>
  );
}

LifecycleActivationProvider.propTypes = { children: PropTypes.node, signedIn: PropTypes.bool };

const FALLBACK = {
  beginActivation: ({ onConfirmed } = {}) => { onConfirmed?.(); },
  summaryVisible: false,
  viewingJobId: null,
  refreshNonce: 0,
  activating: false,
  activate() {},
  cancelActivation() {},
  finishActivation() {},
  openJob() {},
  closeJob() {},
  refreshSummary() {},
};

export function useLifecycleActivation() {
  const ctx = useContext(LifecycleActivationContext);
  return ctx ?? FALLBACK;
}
